// Setup Page - Handle transfer tokens from ChatGPT MCP
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  AlertCircle,
  Loader2,
  ArrowRight,
  Clock,
  Zap,
  Package,
  ExternalLink
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import { useAuth } from '../contexts/AuthContext';
import { getTransfer, claimTransfer, trackEvent } from '../services/api';
import { trackSetupPageView, trackTransferClaim, trackTransferClaimSuccess } from '../services/analytics';

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    mcp_ready: { label: 'MCP Ready', color: 'bg-green-100 text-green-700' },
    api_available: { label: 'API Available', color: 'bg-blue-100 text-blue-700' },
    unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-700' }
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

export default function Setup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signInWithGoogle, loading: authLoading } = useAuth();

  const [transferData, setTransferData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState(null);

  const token = searchParams.get('token');
  const mcpsFromQuery = searchParams.get('mcps');
  const goalFromQuery = searchParams.get('goal');
  const sourceFromQuery = searchParams.get('source');
  const refFromQuery = searchParams.get('ref');

  // Fetch transfer data on mount
  useEffect(() => {
    async function fetchTransfer() {
      // If we have a token, fetch from API
      if (token) {
        try {
          setLoading(true);
          const result = await getTransfer(token);

          if (result.isExpired) {
            setError('This setup link has expired. Please generate a new one from ChatGPT.');
            return;
          }

          setTransferData(result.data);
          setClaimed(result.isClaimed);

          // Track the view (Firestore + Firebase Analytics)
          trackEvent('setup_page_viewed', {
            token,
            mcps: result.data?.mcps,
            source: result.data?.source
          });
          trackSetupPageView(result.data?.source || 'chatgpt', result.data?.mcps?.length || 0);
        } catch (err) {
          setError(err.message || 'Failed to load transfer data');
        } finally {
          setLoading(false);
        }
      }
      // Fallback: parse from query parameters (for when API is unavailable)
      else if (mcpsFromQuery) {
        const mcps = mcpsFromQuery.split(',').map(id => ({
          id: id.trim(),
          name: id.trim().split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          status: 'mcp_ready'
        }));

        setTransferData({
          mcps: mcpsFromQuery.split(','),
          mcpDetails: mcps,
          goal: goalFromQuery ? decodeURIComponent(goalFromQuery) : null,
          source: sourceFromQuery || 'chatgpt'
        });
        setLoading(false);

        trackEvent('setup_page_viewed', {
          mcps: mcpsFromQuery.split(','),
          source: sourceFromQuery || 'direct'
        });
        trackSetupPageView(sourceFromQuery || 'direct', mcpsFromQuery.split(',').length);
      }
      // Handle referral links from artifact (goal + ref parameters)
      else if (goalFromQuery && refFromQuery) {
        setTransferData({
          mcps: [],
          mcpDetails: [],
          goal: decodeURIComponent(goalFromQuery),
          source: refFromQuery
        });
        setLoading(false);

        trackEvent('setup_page_viewed', {
          goal: goalFromQuery,
          source: refFromQuery,
          type: 'artifact_referral'
        });
        trackSetupPageView(refFromQuery, 0);
      }
      else {
        setError('No setup configuration found. Please use a valid setup link from ChatGPT.');
        setLoading(false);
      }
    }

    fetchTransfer();
  }, [token, mcpsFromQuery, goalFromQuery, sourceFromQuery, refFromQuery]);

  // Handle claim action
  const handleClaim = async () => {
    if (!user) {
      // Trigger sign in first
      try {
        await signInWithGoogle();
      } catch (err) {
        setError('Sign in failed. Please try again.');
        return;
      }
    }

    if (token) {
      try {
        setClaiming(true);
        trackTransferClaim(transferData?.mcps?.length || 0);
        await claimTransfer(token);
        setClaimed(true);

        trackEvent('setup_claimed', {
          token,
          mcps: transferData?.mcps,
          userId: user?.uid
        });
        trackTransferClaimSuccess(transferData?.mcps?.length || 0);
      } catch (err) {
        setError(err.message || 'Failed to claim setup');
      } finally {
        setClaiming(false);
      }
    } else {
      // No token - just mark as claimed and proceed
      setClaimed(true);
      trackTransferClaim(0);
      trackEvent('setup_claimed_direct', {
        mcps: transferData?.mcps,
        userId: user?.uid
      });
      trackTransferClaimSuccess(0);
    }
  };

  // Handle sign in
  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err) {
      setError('Sign in failed. Please try again.');
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your setup...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !transferData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Setup Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Go to VinciKit Home
          </button>
        </div>
      </div>
    );
  }

  const mcpDetails = transferData?.mcpDetails || [];
  const mcpCount = mcpDetails.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-bold text-xl">VinciKit</span>
          </div>

          {user && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <img
                src={user.photoURL || '/default-avatar.png'}
                alt=""
                className="w-8 h-8 rounded-full"
              />
              <span>{user.displayName || user.email}</span>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Success state */}
          {claimed ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Setup Complete!
              </h1>
              <p className="text-gray-600 mb-8">
                {mcpCount > 0
                  ? `Your ${mcpCount} MCP${mcpCount > 1 ? 's are' : ' is'} ready to configure.`
                  : 'Your project is ready to configure.'
                }
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/configure')}
                  className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  Configure Your Stack
                  <ArrowRight className="w-5 h-5" />
                </button>

                <button
                  onClick={() => navigate('/home')}
                  className="w-full px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Hero section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  {transferData?.source === 'artifact' ? 'From VinciKit Artifact' : 'From ChatGPT'}
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  {mcpCount > 0
                    ? 'Your Combined MCP Server is Ready'
                    : 'Welcome to VinciKit'
                  }
                </h1>
                <p className="text-lg text-gray-600">
                  {transferData?.goal
                    ? `Build: "${transferData.goal}"`
                    : `${mcpCount} tool${mcpCount > 1 ? 's' : ''} configured and ready to deploy`
                  }
                </p>
              </motion.div>

              {/* MCPs list - only show if there are MCPs */}
              {mcpCount > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-xl p-6 mb-6"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="w-5 h-5 text-gray-600" />
                    <h2 className="font-semibold text-gray-900">Your MCPs</h2>
                  </div>

                  <div className="space-y-3">
                    {mcpDetails.map((mcp, index) => (
                      <motion.div
                        key={mcp.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 font-bold">
                              {mcp.name?.charAt(0) || mcp.id?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{mcp.name}</p>
                            {mcp.description && (
                              <p className="text-sm text-gray-500">{mcp.description}</p>
                            )}
                          </div>
                        </div>
                        <StatusBadge status={mcp.status} />
                      </motion.div>
                    ))}
                  </div>

                  {transferData?.expiresAt && (
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>
                        Expires: {new Date(transferData.expiresAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Goal info card - show when no MCPs but goal is present */}
              {mcpCount === 0 && transferData?.goal && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-xl p-6 mb-6"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <h2 className="font-semibold text-gray-900">Your Project</h2>
                  </div>
                  <p className="text-gray-600 mb-4">
                    You're building: <span className="font-medium text-gray-900">"{transferData.goal}"</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    Sign in to get personalized tool recommendations, setup guides, and API key management for your project.
                  </p>
                </motion.div>
              )}

              {/* Action section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                {!user ? (
                  <div className="text-center">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Sign in to claim your setup
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm">
                      One-click sign in with Google to save your configuration
                    </p>

                    <button
                      onClick={handleSignIn}
                      className="w-full px-6 py-4 bg-white border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-3"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Ready to set up your combined MCP server?
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm">
                      Click below to claim your configuration and get started
                    </p>

                    <button
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {claiming ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5" />
                          Claim & Configure
                        </>
                      )}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}
              </motion.div>

              {/* Info section */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-8 text-center text-sm text-gray-500"
              >
                <p>
                  Your setup was created from{' '}
                  <span className="font-medium">
                    {transferData?.source === 'chatgpt-mcp' ? 'ChatGPT'
                      : transferData?.source === 'artifact' ? 'VinciKit Artifact'
                      : transferData?.source || 'an MCP client'}
                  </span>
                </p>
                <a
                  href="https://vincikit.com/pricing"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2"
                >
                  View pricing & plans
                  <ExternalLink className="w-3 h-3" />
                </a>
                <span className="mx-2">·</span>
                <a
                  href="https://vincikit.com/feedback"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2"
                >
                  Give feedback
                  <ExternalLink className="w-3 h-3" />
                </a>
              </motion.div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}